import { MARKET_TYPES, STATUS_META, formatChancePct } from "./bondPredictHelpers";
import { formatAppDateTime } from "./appTimeFormat";

export const OPEN_ORDER_STATUSES = new Set(["OPEN", "PARTIALLY_FILLED"]);

export const ORDER_STATUS_META = {
  OPEN: { label: "Open", tone: "live" },
  PARTIALLY_FILLED: { label: "Partial", tone: "pending" },
  FILLED: { label: "Filled", tone: "final" },
  CANCELLED: { label: "Cancelled", tone: "closed" },
  EXPIRED: { label: "Expired", tone: "closed" },
  REJECTED: { label: "Rejected", tone: "closed" },
};

export const isOpenOrder = (order) =>
  OPEN_ORDER_STATUSES.has(String(order?.status || "").toUpperCase());

export const isCancellableOrder = (order, market, contractLine) => {
  if (!isOpenOrder(order)) return false;
  const lineStatus = effectiveStatusFor(market, contractLine);
  return !["CLOSED", "RESOLVED", "FINALIZED", "VOID", "REFUNDED"].includes(lineStatus);
};

export const formatCoin = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `Coin ${n.toFixed(2)}`;
};

export const formatCoinAbbrev = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `Coin ${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `Coin ${(n / 1_000).toFixed(1)}K`;
  return formatCoin(n);
};

export const formatSignedCoin = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const sign = n >= 0 ? "+" : "−";
  return `${sign}Coin ${Math.abs(n).toFixed(2)}`;
};

export const formatPrice01 = (price) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(4);
};

export { formatChancePct };

export const formatPctChange = (value, base) => {
  const v = Number(value);
  const b = Number(base);
  if (!Number.isFinite(v) || !Number.isFinite(b) || b === 0) return null;
  return (v / b) * 100;
};

export const formatShortDate = (value) => {
  if (!value) return "—";
  try {
    return formatAppDateTime(value);
  } catch {
    return "—";
  }
};

export const cleanLineLabel = (rawLabel, selectionKey) =>
  String(rawLabel || selectionKey || "")
    .trim()
    .replace(/\s*\((Yes|No)\)\s*$/i, "")
    .replace(/\s+(Yes|No)$/i, "")
    .trim();

export const findContractLine = (market, selectionKey) => {
  if (!selectionKey || !Array.isArray(market?.contractLines)) return null;
  return (
    market.contractLines.find((l) => String(l?.selectionKey) === String(selectionKey)) ||
    null
  );
};

export const findLineResolution = (market, selectionKey) => {
  if (!selectionKey || !Array.isArray(market?.multiBinaryResolutions)) return null;
  return (
    market.multiBinaryResolutions.find(
      (r) => String(r?.selectionKey) === String(selectionKey)
    ) || null
  );
};

export const effectiveStatusFor = (market, contractLine) => {
  const marketStatus = String(market?.status || "OPEN").toUpperCase();
  if (market?.type !== MARKET_TYPES.MULTI_BINARY || !contractLine) return marketStatus;
  const lineStatus = String(contractLine.status || "").toUpperCase();
  return lineStatus || marketStatus;
};

export const didOutcomeWin = (outcome, lineResolution) => {
  if (!lineResolution) return null;
  if (String(lineResolution.status || "").toUpperCase() === "VOID") return null;
  const winnerId = lineResolution.winningOutcome
    ? String(lineResolution.winningOutcome)
    : null;
  const outcomeId = outcome?._id ? String(outcome._id) : null;
  if (!winnerId || !outcomeId) return null;
  return outcomeId === winnerId;
};

export const getPositionOutcomeLabel = (position) => {
  const market = position?.market || {};
  const outcome = position?.outcome || {};
  const isMulti = market?.type === MARKET_TYPES.MULTI_BINARY;
  const outcomeSide = String(outcome?.side || "").toUpperCase();
  const rawOutcomeLabel = outcome?.label || outcomeSide || "Outcome";
  const contractLine = isMulti ? findContractLine(market, outcome?.selectionKey) : null;
  const lineLabel = isMulti
    ? contractLine?.label || cleanLineLabel(rawOutcomeLabel, outcome?.selectionKey)
    : null;
  const sideShort = outcomeSide === "YES" ? "Yes" : outcomeSide === "NO" ? "No" : outcomeSide;

  if (isMulti && lineLabel) {
    return `${lineLabel} · ${sideShort || ""}`.trim().replace(/[· ]+$/, "");
  }
  if (market?.type === MARKET_TYPES.HEAD_TO_HEAD) {
    return rawOutcomeLabel.replace(/\s*\((Yes|No)\)\s*$/i, "").trim() || sideShort;
  }
  return cleanLineLabel(rawOutcomeLabel) || sideShort || "Outcome";
};

export const getOrderOutcomeLabel = (order) => {
  const market = order?.market || {};
  const outcome = order?.outcome || {};
  return getPositionOutcomeLabel({ market, outcome });
};

export const getMarketTitle = (market) =>
  market?.title || market?.question || "Market";

export const getStatusMeta = (status) =>
  STATUS_META[String(status || "").toUpperCase()] || {
    label: status || "—",
    tone: "muted",
  };

const MARKET_STATUS_SORT = {
  OPEN: 0,
  CLOSED: 1,
  ON_HOLD: 2,
  RESOLVED: 3,
  FINALIZED: 4,
  VOID: 5,
  REFUNDED: 6,
};

const ORDER_STATUS_SORT = { OPEN: 0, PARTIALLY_FILLED: 1 };

export const sortPositions = (positions = []) =>
  [...positions].sort((a, b) => {
    const lineA = findContractLine(a?.market, a?.outcome?.selectionKey);
    const lineB = findContractLine(b?.market, b?.outcome?.selectionKey);
    const sa = MARKET_STATUS_SORT[effectiveStatusFor(a?.market, lineA)] ?? 9;
    const sb = MARKET_STATUS_SORT[effectiveStatusFor(b?.market, lineB)] ?? 9;
    if (sa !== sb) return sa - sb;
    const tb = new Date(b?.lastTradeAt || 0).getTime();
    const ta = new Date(a?.lastTradeAt || 0).getTime();
    if (tb !== ta) return tb - ta;
    return Number(b?.invested || 0) - Number(a?.invested || 0);
  });

export const sortOrders = (orders = []) =>
  [...orders].sort((a, b) => {
    const ra = ORDER_STATUS_SORT[String(a?.status || "").toUpperCase()] ?? 9;
    const rb = ORDER_STATUS_SORT[String(b?.status || "").toUpperCase()] ?? 9;
    if (ra !== rb) return ra - rb;
    return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
  });

const SETTLED_STATUSES = new Set(["CLOSED", "RESOLVED", "FINALIZED", "VOID", "REFUNDED"]);
const DONE_ORDER_STATUSES = new Set(["FILLED", "CANCELLED", "EXPIRED", "REJECTED"]);

export const DEFAULT_HOLDINGS_FILTERS = {
  scope: "all",
  marketType: "",
  marketStatus: "",
  category: "",
  pnl: "",
  sort: "recent",
};

export const DEFAULT_ORDERS_FILTERS = {
  scope: "all",
  side: "",
  orderType: "",
  orderStatus: "",
  marketType: "",
  category: "",
  sort: "recent",
};

export const HOLDINGS_SCOPE_OPTIONS = [
  { value: "all", label: "All positions", hint: "Every holding" },
  { value: "live", label: "Live", hint: "Open markets" },
  { value: "settled", label: "Settled", hint: "Resolved lines" },
];

export const HOLDINGS_PNL_OPTIONS = [
  { value: "", label: "Any P&L", hint: "All outcomes" },
  { value: "profit", label: "In profit", hint: "Positive return" },
  { value: "loss", label: "In loss", hint: "Negative return" },
  { value: "flat", label: "Break-even", hint: "Near zero P&L" },
];

export const HOLDINGS_SORT_OPTIONS = [
  { value: "recent", label: "Recent activity", hint: "Last trade first" },
  { value: "invested", label: "Most invested", hint: "Largest stake" },
  { value: "pnl", label: "Highest P&L", hint: "Top performers" },
];

export const ORDERS_SCOPE_OPTIONS = [
  { value: "all", label: "All orders", hint: "Full history" },
  { value: "working", label: "Working", hint: "Open & partial" },
  { value: "done", label: "Done", hint: "Filled & closed" },
];

export const ORDER_SIDE_OPTIONS = [
  { value: "", label: "Any side", hint: "Buy or sell" },
  { value: "BUY", label: "Buy", hint: "Long-side orders" },
  { value: "SELL", label: "Sell", hint: "Exit-side orders" },
];

export const ORDER_TYPE_OPTIONS = [
  { value: "", label: "Any type", hint: "Limit or market" },
  { value: "LIMIT", label: "Limit", hint: "Named price" },
  { value: "MARKET", label: "Market", hint: "At best price" },
];

export const ORDER_STATUS_FILTER_OPTIONS = [
  { value: "", label: "Any status", hint: "Every state" },
  { value: "OPEN", label: "Open", hint: "Awaiting fill" },
  { value: "PARTIALLY_FILLED", label: "Partial", hint: "Partly matched" },
  { value: "FILLED", label: "Filled", hint: "Fully matched" },
  { value: "CANCELLED", label: "Cancelled", hint: "User cancelled" },
  { value: "EXPIRED", label: "Expired", hint: "Timed out" },
  { value: "REJECTED", label: "Rejected", hint: "Desk rejected" },
];

export const ORDERS_SORT_OPTIONS = [
  { value: "recent", label: "Newest first", hint: "Latest placed" },
  { value: "oldest", label: "Oldest first", hint: "Earliest placed" },
  { value: "unfilled", label: "Most unfilled", hint: "Largest remainder" },
];

export const PORTFOLIO_MARKET_TYPE_OPTIONS = [
  { value: "", label: "All types", hint: "Any market shape" },
  { value: "BINARY", label: "Yes / No", hint: "Binary contracts" },
  { value: "MULTI_BINARY", label: "Multi", hint: "Several lines" },
  { value: "HEAD_TO_HEAD", label: "H2H", hint: "Side vs side" },
];

export const PORTFOLIO_MARKET_STATUS_OPTIONS = [
  { value: "", label: "Any market status", hint: "Every desk state" },
  { value: "OPEN", label: "Live", hint: "Open for trading" },
  { value: "CLOSED", label: "Closed", hint: "Awaiting outcome" },
  { value: "RESOLVED", label: "Settling", hint: "Outcome pending" },
  { value: "FINALIZED", label: "Settled", hint: "Final payout" },
  { value: "VOID", label: "Void", hint: "Cancelled desk" },
  { value: "ON_HOLD", label: "On hold", hint: "Paused markets" },
];

const matchesSearch = (haystack, q) => !q || haystack.includes(q);

const sortFilteredHoldings = (rows, sortKey) => {
  const copy = [...rows];
  if (sortKey === "invested") {
    return copy.sort((a, b) => Number(b?.invested || 0) - Number(a?.invested || 0));
  }
  if (sortKey === "pnl") {
    return copy.sort((a, b) => Number(b?.realizedPnL || 0) - Number(a?.realizedPnL || 0));
  }
  return sortPositions(copy);
};

const sortFilteredOrders = (rows, sortKey) => {
  const copy = [...rows];
  if (sortKey === "oldest") {
    return copy.sort(
      (a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime()
    );
  }
  if (sortKey === "unfilled") {
    return copy.sort((a, b) => {
      const remA = Math.max(0, Number(a?.quantity || 0) - Number(a?.filledQuantity || 0));
      const remB = Math.max(0, Number(b?.quantity || 0) - Number(b?.filledQuantity || 0));
      return remB - remA;
    });
  }
  return sortOrders(copy);
};

export const collectPortfolioCategories = (items = []) => {
  const seen = new Map();
  for (const item of items) {
    const cat = item?.market?.category;
    if (!cat) continue;
    const key = String(cat);
    if (!seen.has(key)) {
      seen.set(key, key.replace(/_/g, " "));
    }
  }
  return [
    { value: "", label: "All categories", hint: "Every sport & topic" },
    ...[...seen.entries()].map(([value, label]) => ({
      value,
      label,
      hint: `${label} markets`,
    })),
  ];
};

export const countPortfolioFiltersActive = (tab, holdingsFilters, ordersFilters, search = "") => {
  let n = search.trim() ? 1 : 0;
  const hf = holdingsFilters || DEFAULT_HOLDINGS_FILTERS;
  const of = ordersFilters || DEFAULT_ORDERS_FILTERS;
  if (tab === "holdings") {
    if (hf.scope !== "all") n += 1;
    if (hf.marketType) n += 1;
    if (hf.marketStatus) n += 1;
    if (hf.category) n += 1;
    if (hf.pnl) n += 1;
    if (hf.sort !== "recent") n += 1;
  } else {
    if (of.scope !== "all") n += 1;
    if (of.side) n += 1;
    if (of.orderType) n += 1;
    if (of.orderStatus) n += 1;
    if (of.marketType) n += 1;
    if (of.category) n += 1;
    if (of.sort !== "recent") n += 1;
  }
  return n;
};

export const applyHoldingsFilters = (positions, filters = DEFAULT_HOLDINGS_FILTERS, search = "") => {
  const q = search.trim().toLowerCase();
  const f = { ...DEFAULT_HOLDINGS_FILTERS, ...filters };
  const rows = positions.filter((p) => {
    const market = p?.market || {};
    const contractLine = findContractLine(market, p?.outcome?.selectionKey);
    const status = effectiveStatusFor(market, contractLine);
    if (f.scope === "live" && SETTLED_STATUSES.has(status)) return false;
    if (f.scope === "settled" && !SETTLED_STATUSES.has(status)) return false;
    if (f.marketType && String(market?.type || "") !== f.marketType) return false;
    if (f.marketStatus && status !== f.marketStatus) return false;
    if (f.category && String(market?.category || "") !== f.category) return false;
    const pnl = Number(p?.realizedPnL || 0);
    if (f.pnl === "profit" && pnl <= 0) return false;
    if (f.pnl === "loss" && pnl >= 0) return false;
    if (f.pnl === "flat" && pnl !== 0) return false;
    const hay = [getMarketTitle(market), getPositionOutcomeLabel(p)].join(" ").toLowerCase();
    return matchesSearch(hay, q);
  });
  return sortFilteredHoldings(rows, f.sort);
};

export const applyOrdersFilters = (orders, filters = DEFAULT_ORDERS_FILTERS, search = "") => {
  const q = search.trim().toLowerCase();
  const f = { ...DEFAULT_ORDERS_FILTERS, ...filters };
  const rows = orders.filter((o) => {
    const status = String(o?.status || "").toUpperCase();
    if (f.scope === "working" && !isOpenOrder(o)) return false;
    if (f.scope === "done" && !DONE_ORDER_STATUSES.has(status)) return false;
    if (f.side && String(o?.side || "").toUpperCase() !== f.side) return false;
    if (f.orderType && String(o?.type || "").toUpperCase() !== f.orderType) return false;
    if (f.orderStatus && status !== f.orderStatus) return false;
    const market = o?.market || {};
    if (f.marketType && String(market?.type || "") !== f.marketType) return false;
    if (f.category && String(market?.category || "") !== f.category) return false;
    const hay = [getMarketTitle(market), getOrderOutcomeLabel(o)].join(" ").toLowerCase();
    return matchesSearch(hay, q);
  });
  return sortFilteredOrders(rows, f.sort);
};

/** @deprecated Use applyHoldingsFilters */
export const filterPositions = (positions, filter, search = "") =>
  applyHoldingsFilters(positions, { ...DEFAULT_HOLDINGS_FILTERS, scope: filter || "all" }, search);

/** @deprecated Use applyOrdersFilters */
export const filterOrders = (orders, filter, search = "") =>
  applyOrdersFilters(orders, { ...DEFAULT_ORDERS_FILTERS, scope: filter || "all" }, search);

export const buildCancelPreview = (order) => {
  if (!order) return null;
  const quantity = Number(order?.quantity || 0);
  const filled = Number(order?.filledQuantity || 0);
  const remaining = Math.max(0, quantity - filled);
  const price = Number(order?.price);
  const side = String(order?.side || "").toUpperCase();
  const type = String(order?.type || "LIMIT").toUpperCase();
  const market = order?.market || {};
  return {
    quantity,
    filled,
    remaining,
    price,
    side,
    type,
    isPartial: filled > 0 && remaining > 0,
    marketTitle: getMarketTitle(market),
    outcomeLabel: getOrderOutcomeLabel(order),
    refundEstimate: side === "BUY" && Number.isFinite(price) ? remaining * price : null,
  };
};
